import * as React from 'react';
import { SwitchBase, SwitchBaseProps } from '../SwitchBase/SwitchBase';
import { SwitchChipVariant, useComponentDefaultProps, useHoneycombTheme } from '../../theming';
import { FixMeLater } from '../../utils';
import { useStyles } from './SwitchChipBase.styles';
import { Chip, ChipProps } from '../Chip/Chip';
import { useId } from '../../hooks';
import { SwitchContainerContext, SwitchContainerContextValues } from '../SwitchBase/SwitchContainer.context';

export interface SwitchChipBaseProps extends SwitchBaseProps {
    /**
     * The label which will be displayed on the chip.
     */
    children?: React.ReactNode;

    /**
     * If `true`, the component will be disabled.
     */
    disabled?: boolean;

    /**
     * The `id` attribute of the `input` element.
     */
    id?: string;

    /**
     * Additional properties applies to the `chip` component.
     */
    ChipProps?: ChipProps;

    /**
     * The style variant. This can be configured at theme level.
     */
    variant?: SwitchChipVariant;
}

const defaultProps: Partial<SwitchChipBaseProps> = {};

export const SwitchChipBase = React.forwardRef(function SwitchChipBase(
    props: SwitchChipBaseProps,
    ref: React.ForwardedRef<FixMeLater>
): React.JSX.Element {
    const {
        children,
        disabled,
        id: idProp,
        ChipProps: ChipPropsProp,
        variant = 'default',
        checked: checkedProp,
        defaultChecked,
        ...other
    } = useComponentDefaultProps('SwitchChipBase', defaultProps, props);

    const id = useId(idProp);

    const [checked, setChecked] = React.useState<boolean>(Boolean(checkedProp || defaultChecked));

    const { styles } = useStyles();

    const { theme } = useHoneycombTheme();
    const componentTheme = theme.components.themes.Chip;
    const variantProps = componentTheme.switchChipVariant[variant];
    const { color: chipColor, variant: chipVariant } = variantProps[checked ? 'checked' : 'unchecked'];

    const contextValues: SwitchContainerContextValues = React.useMemo(
        () => ({
            setChecked,
            setFocused: null,
        }),
        []
    );

    return (
        <SwitchContainerContext.Provider value={contextValues}>
            <Chip
                ref={ref}
                component="label"
                control={
                    <SwitchBase
                        css={styles.input}
                        id={id}
                        checked={checkedProp}
                        defaultChecked={defaultChecked}
                        disabled={disabled}
                        {...other}
                    />
                }
                color={chipColor}
                variant={chipVariant}
                {...ChipPropsProp}
            >
                {children}
            </Chip>
        </SwitchContainerContext.Provider>
    );
});
